import React, { useState, useEffect } from 'react';
import {
    Box,
    ImageList,
    ImageListItem,
    Skeleton,
    Modal,
    IconButton,
    Paper,
    Typography,
    Stack
} from '@mui/material';
import { get } from '../../utility/fetchWeb';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';

const LoadingPlaceholder = () => (
    <ImageListItem>
        <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
            sx={{ borderRadius: 2 }}
        />
    </ImageListItem>
);

const Media = ({ groupId }) => {
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

    const fetchMedia = async () => {
        try {
            setLoading(true);
            const response = await get(`/Posts/media?groupId=${groupId}`);
            if (response) {
                setMedia(response?.posts || []);
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            setMedia([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (groupId) {
            fetchMedia();
        }
    }, [groupId]);

    const handleMediaClick = (index) => {
        setSelectedMediaIndex(index);
        setModalOpen(true);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setSelectedMediaIndex((prev) =>
            prev === media.length - 1 ? 0 : prev + 1
        );
    };

    const handlePrevious = (e) => {
        e.stopPropagation();
        setSelectedMediaIndex((prev) =>
            prev === 0 ? media.length - 1 : prev - 1
        );
    };

    return (
        <Box sx={{ width: '100%', overflowY: 'hidden' }}>
            {loading ? (
                <ImageList variant="masonry" cols={3} gap={8} sx={{ m: 0 }}>
                    {[...Array(6)].map((_, index) => (
                        <LoadingPlaceholder key={index} />
                    ))}
                </ImageList>
            ) : media.length === 0 ? (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        minHeight: 300,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Media Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        This group doesn't have any photos or videos yet
                    </Typography>
                </Stack>
            ) : (
                <>
                    <ImageList
                        variant="masonry"
                        cols={3}
                        gap={8}
                        sx={{
                            m: 0,
                            '@media (max-width: 600px)': {
                                cols: 2,
                            }
                        }}
                    >
                        {media.map((item, index) => (
                            <ImageListItem
                                key={item.id}
                                onClick={() => handleMediaClick(index)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 0.9,
                                        transform: 'scale(1.02)',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                            >
                                {item.mediaType?.startsWith('image/') ? (
                                    <img
                                        src={`${process.env.REACT_APP_MEDIA_URL}${item.mediaUrl}`}
                                        alt={`${item.groupName} media ${index + 1}`}
                                        loading="lazy"
                                        style={{ borderRadius: 8 }}
                                    />
                                ) : (
                                    <video
                                        src={`${process.env.REACT_APP_MEDIA_URL}${item.mediaUrl}`}
                                        controls
                                        style={{ width: '100%', borderRadius: 8 }}
                                    />
                                )}
                            </ImageListItem>
                        ))}
                    </ImageList>

                    <Modal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 'auto',
                            '& .MuiBackdrop-root': {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            }
                        }}
                    >
                        <Paper
                            sx={{
                                position: 'relative',
                                width: { xs: '90vw', sm: '80vw', md: '60vw' },
                                height: { xs: '80vh', sm: '70vh' },
                                bgcolor: 'background.paper',
                                p: 2,
                                outline: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto', // Add horizontal margin auto
                                left: '38%', // Center relative to viewport
                                transform: 'translateX(-50%)', // Offset by half width
                                boxShadow: 24,
                                borderRadius: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    zIndex: 1,
                                }}
                                onClick={() => setModalOpen(false)}
                            >
                                <CloseIcon color='error' />
                            </IconButton>

                            <Box 
                                sx={{ 
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}
                            >
                                {media[selectedMediaIndex]?.mediaType?.startsWith('image/') ? (
                                    <img
                                        src={`${process.env.REACT_APP_MEDIA_URL}${media[selectedMediaIndex]?.mediaUrl}`}
                                        alt={`Group media ${selectedMediaIndex + 1}`}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                ) : (
                                    <video
                                        src={`${process.env.REACT_APP_MEDIA_URL}${media[selectedMediaIndex]?.mediaUrl}`}
                                        controls
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            display: 'block',
                                        }}
                                    />
                                )}
                            </Box>

                            {media.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={handlePrevious}
                                        sx={{
                                            position: 'absolute',
                                            left: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(22, 102, 18, 0.9)',
                                            color: 'white',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                bgcolor: 'rgba(22, 102, 18, 0.19)',
                                                color: 'rgba(22, 102, 18, 0.9)',
                                            },
                                        }}
                                    >
                                        <ArrowBackIosNewIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={handleNext}
                                        sx={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(22, 102, 18, 0.9)',
                                            color: 'white',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                bgcolor: 'rgba(22, 102, 18, 0.19)',
                                                color: 'rgba(22, 102, 18, 0.9)',
                                            },
                                        }}
                                    >
                                        <ArrowForwardIosIcon />
                                    </IconButton>
                                </>
                            )}
                        </Paper>
                    </Modal>
                </>
            )}
        </Box>
    );
};

export default Media;